import { type Comment, type ReactionGroup } from '../api/code-forge/interface';
import { Markdown } from './markdown';

const reactionEmojiDescriptionMap = {
    '👍': 'thumbs up',
    '👎': 'thumbs down',
    '😄': 'laughing',
    '🎉': 'hooray',
    '😕': 'confused',
    '❤️': 'heart',
    '🚀': 'rocket',
    '👀': 'eyes',
};
const formatReactionTitle = (reactionGroup: ReactionGroup) => {
    const { reaction, users, count } = reactionGroup;

    let userListString: string;
    if (count === 1) userListString = users[0] || 'Someone';
    else if (count <= 5) userListString = users.join(', ');
    else {
        const displayedUsers = users.slice(0, 5).join(', ');
        const remainingCount = count - 5;
        userListString = `${displayedUsers}, and ${remainingCount} others`;
    }

    const reactionDescription =
        reaction in reactionEmojiDescriptionMap ? reactionEmojiDescriptionMap[reaction as '👍'] + ' emoji' : reaction;

    return `${userListString} reacted with ${reactionDescription}`;
};

export type CommentCardProps = {
    comment: Comment;
    className?: string;
    /** Necessary to display child-comments. */
    allComments?: Comment[];
};

export const CommentCard = ({ comment, className, allComments }: CommentCardProps) => {
    const cardBorderClass =
        comment.type === 'issue_body'
            ? 'border-info'
            : comment.type === 'review_approved'
            ? 'border-success'
            : comment.type === 'review_changes_requested'
            ? 'border-danger'
            : comment.type === 'review_commented'
            ? 'border-secondary'
            : '';

    const childComments = allComments?.filter((child) => child.parentId === comment.id);

    return (
        <div key={comment.id} className={`card mb-3 ${cardBorderClass} ${className || ''}`}>
            <div className="card-body">
                <h5 className="card-title d-flex justify-content-between align-items-center">
                    <div className="text-muted fs-6">
                        {comment.user || 'Someone'}
                        {comment.type === 'review_approved' && <span className="fw-normal"> approved</span>}
                        {comment.type === 'review_changes_requested' && (
                            <span className="fw-normal"> requested changes</span>
                        )}
                        {comment.type === 'review_commented' && <span className="fw-normal"> commented</span>}
                        {comment.type === 'issue_body' && <span className="fw-normal"> opened PR</span>} on{' '}
                        <a href={comment.url} target="_blank" rel="noopener noreferrer" className="text-muted">
                            {new Date(comment.createdAt).toLocaleString()}
                        </a>
                    </div>

                    {comment.userAssociation && (
                        <span className="badge border border-dark text-dark fw-normal">{comment.userAssociation}</span>
                    )}
                </h5>

                {comment.body && (
                    <div className="card-text mb-2">
                        <Markdown markdown={comment.body} />
                    </div>
                )}

                {comment.reactions && comment.reactions.length > 0 && (
                    <div className="mb-2">
                        {comment.reactions.map((reactionGroup) => (
                            <span
                                key={reactionGroup.reaction}
                                className="badge bg-light text-dark me-1"
                                title={formatReactionTitle(reactionGroup)}>
                                {reactionGroup.reaction} {reactionGroup.count}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {childComments && childComments.length > 0 && (
                <div className="mx-4">
                    {childComments.map((child) => (
                        <CommentCard key={child.id} comment={child} allComments={allComments} />
                    ))}
                </div>
            )}
        </div>
    );
};
